import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, Check } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";

// Fallback self-hosted (inline SVG, fără hotlink extern) pentru coperta cursului recomandat
const COVER_FALLBACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='1500'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%230D1B2A'/%3E%3Cstop offset='1' stop-color='%230E7C62'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23g)'/%3E%3C/svg%3E";

export default function CoursesPreview() {
  const { sectionContent } = useSite();
  const c = sectionContent("courses_preview");
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/public/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  if (!c) return null;
  const featured = courses[0];
  const rest = courses.slice(1);

  return (
    <section data-testid="courses-preview" className="py-24 lg:py-32" style={{ background: "var(--ct-bg)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-4 mb-3">
              <span className="gold-rule" />
              <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c.eyebrow || "Cursuri online"}</span>
            </div>
            <h2 data-el="title" data-field="title" className="font-serif text-4xl sm:text-5xl leading-tight">{c.title || "Programe structurate de transformare"}</h2>
            {c.subtitle && <p data-el="subtitle" data-field="subtitle" className="text-base leading-relaxed mt-3 max-w-2xl" style={{ color: "var(--ct-text-muted)" }}>{c.subtitle}</p>}
          </div>
          <Link to="/cursuri" className="btn-secondary" data-testid="courses-see-all">
            <span data-field="see_all_label">{c.see_all_label || "Toate cursurile"}</span> <ChevronRight size={16} />
          </Link>
        </div>

        {courses.length === 0 ? (
          <p style={{ color: "var(--ct-text-muted)" }}>Cursurile vor fi publicate în curând.</p>
        ) : (
          <div className="space-y-5">
            {featured && (
              <Link
                to={`/cursuri/${featured.slug}`}
                className="block overflow-hidden rounded-2xl relative"
                style={{ background: "var(--ct-emerald)", color: "var(--ct-text-inverse)", border: "1.5px solid var(--ct-gold)" }}
                data-testid="featured-course"
              >
                {/* subtle geometric pattern overlay */}
                <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{
                  backgroundImage: "radial-gradient(circle at 18% 28%, var(--ct-gold) 0px, transparent 2px), radial-gradient(circle at 78% 72%, var(--ct-gold) 0px, transparent 2px)",
                  backgroundSize: "40px 40px",
                }} />
                <div className="grid grid-cols-[38%_62%] md:grid-cols-[42%_58%] gap-0 relative">
                  <div className="aspect-[3/4] md:aspect-auto overflow-hidden">
                    <img
                      src={featured.cover_image || COVER_FALLBACK}
                      alt={featured.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 sm:p-7 lg:p-12 flex flex-col justify-center">
                    <span data-field="featured_badge" className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.28em] mb-2 sm:mb-4" style={{ color: "var(--ct-gold)" }}>{c.featured_badge || "★ Curs recomandat"}</span>
                    <h3 className="font-serif text-2xl sm:text-4xl lg:text-5xl mb-2 sm:mb-4 leading-[0.95]" style={{ color: "#F7F5F0", textShadow: "0 1px 2px rgba(0,0,0,0.18)" }}>{featured.title}</h3>
                    <p className="text-[11px] sm:text-base leading-snug sm:leading-relaxed mb-2.5 sm:mb-6 line-clamp-2 sm:line-clamp-none" style={{ color: "#E4C568" }}>{featured.subtitle}</p>
                    {Array.isArray(featured.what_includes) && featured.what_includes.length > 0 && (
                      <ul className="space-y-1 sm:space-y-2 mb-2.5 sm:mb-6 hidden sm:block">
                        {featured.what_includes.slice(0, 3).map((w, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Sparkles size={14} className="mt-0.5 shrink-0" style={{ color: "var(--ct-gold)" }} />
                            <span style={{ color: "var(--ct-text-inverse)" }}>{w}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <span className="btn-primary self-start !py-1.5 !px-3 !text-[11px] sm:!py-2.5 sm:!px-4 sm:!text-sm" style={{ background: "var(--ct-gold)", color: "#0D1B2A", borderColor: "var(--ct-gold)" }}>
                      <span data-field="featured_cta">{c.featured_cta || "Intră în curs"}</span> <ChevronRight size={12} className="sm:hidden" /><ChevronRight size={18} className="hidden sm:inline" />
                    </span>
                  </div>
                </div>
              </Link>
            )}

            {rest.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {rest.slice(0, 3).map((course, i) => (
                  <Link
                    key={course.id}
                    to={`/cursuri/${course.slug}`}
                    className="card-premium overflow-hidden flex flex-col"
                    data-testid={`course-card-${i}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={course.cover_image} alt={course.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-7 flex-1 flex flex-col">
                      <h3 className="font-serif text-2xl mb-2 tracking-tight">{course.title}</h3>
                      {course.subtitle && <p className="text-sm leading-relaxed flex-1" style={{ color: "var(--ct-text-muted)" }}>{course.subtitle}</p>}
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--ct-link)" }}>
                        <span data-field="card_cta">{c.card_cta || "Vezi cursul"}</span> <ChevronRight size={14} />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
